/**
 * BackgroundRippleEffect Component
 *
 * Remotion-native ripple effect that creates a grid of animated dots/shapes
 * emanating outward from a trigger point. Perfect for product reveal moments.
 *
 * Based on Framer's BackgroundRippleEffect concept, adapted for video rendering.
 */

import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, Easing } from 'remotion';

export type RippleShape = 'circle' | 'square' | 'diamond';

export interface BackgroundRippleEffectProps {
  /** Frame when the ripple animation starts */
  startFrame?: number;
  /** Number of columns in the grid */
  cols?: number;
  /** Number of rows in the grid */
  rows?: number;
  /** Size of each cell in pixels */
  cellSize?: number;
  /** Gap between cells */
  gap?: number;
  /** Base color of the dots */
  color?: string;
  /** Shape of each cell */
  shape?: RippleShape;
  /** Duration of each cell's animation in seconds */
  animationDuration?: number;
  /** Delay multiplier based on distance (higher = slower spread) */
  delayMultiplier?: number;
  /** Maximum scale of cells during animation */
  maxScale?: number;
  /** Center X position (0-100%) where ripple originates */
  originX?: number;
  /** Center Y position (0-100%) where ripple originates */
  originY?: number;
  /** Whether to fade out after animation completes */
  fadeOut?: boolean;
  /** Frame when fade out begins (relative to animation end) */
  fadeOutDelay?: number;
  /** Opacity of cells at rest */
  restOpacity?: number;
  /** Opacity of cells at peak animation */
  peakOpacity?: number;
  /** Shadow blur radius */
  shadowBlur?: number;
  /** Shadow color */
  shadowColor?: string;
}

interface CellData {
  x: number;
  y: number;
  col: number;
  row: number;
  distance: number;
  delay: number;
}

/**
 * Render a single shape
 */
const ShapeRenderer: React.FC<{
  shape: RippleShape;
  size: number;
  color: string;
  opacity: number;
  scale: number;
  shadowBlur: number;
  shadowColor: string;
}> = ({ shape, size, color, opacity, scale, shadowBlur, shadowColor }) => {
  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    opacity,
    transform: `scale(${scale})`,
    boxShadow: shadowBlur > 0 ? `0 0 ${shadowBlur}px ${shadowColor}` : undefined,
  };

  if (shape === 'circle') {
    return (
      <div
        style={{
          ...baseStyle,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
    );
  }

  if (shape === 'square') {
    return (
      <div
        style={{
          ...baseStyle,
          borderRadius: size * 0.15,
          backgroundColor: color,
        }}
      />
    );
  }

  if (shape === 'diamond') {
    return (
      <div
        style={{
          ...baseStyle,
          backgroundColor: color,
          transform: `scale(${scale}) rotate(45deg)`,
          borderRadius: size * 0.1,
        }}
      />
    );
  }

  return null;
};

export const BackgroundRippleEffect: React.FC<BackgroundRippleEffectProps> = ({
  startFrame = 0,
  cols = 12,
  rows = 8,
  cellSize = 8,
  gap = 24,
  color = 'rgba(255, 255, 255, 0.6)',
  shape = 'circle',
  animationDuration = 0.4,
  delayMultiplier = 0.08,
  maxScale = 2.5,
  originX = 50,
  originY = 0,
  fadeOut = true,
  fadeOutDelay = 30,
  restOpacity = 0.15,
  peakOpacity = 0.8,
  shadowBlur = 8,
  shadowColor = 'rgba(255, 255, 255, 0.4)',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate animation duration in frames
  const animDurationFrames = animationDuration * fps;

  // Generate grid cells with their distances from origin
  const cells = useMemo<CellData[]>(() => {
    const result: CellData[] = [];
    const totalWidth = cols * (cellSize + gap);
    const totalHeight = rows * (cellSize + gap);

    // Origin point in grid coordinates
    const originCol = (originX / 100) * cols;
    const originRow = (originY / 100) * rows;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Calculate position
        const x = col * (cellSize + gap) + cellSize / 2;
        const y = row * (cellSize + gap) + cellSize / 2;

        // Calculate Euclidean distance from origin
        const dx = col - originCol;
        const dy = row - originRow;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Calculate delay based on distance
        const delay = distance * delayMultiplier * fps;

        result.push({ x, y, col, row, distance, delay });
      }
    }

    return result;
  }, [cols, rows, cellSize, gap, originX, originY, delayMultiplier, fps]);

  // Find max distance for normalization
  const maxDistance = useMemo(() => {
    return Math.max(...cells.map((c) => c.distance));
  }, [cells]);

  // Calculate total animation duration (last cell delay + animation duration)
  const totalAnimDuration = maxDistance * delayMultiplier * fps + animDurationFrames;

  // Calculate grid dimensions
  const gridWidth = cols * (cellSize + gap) - gap;
  const gridHeight = rows * (cellSize + gap) - gap;

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        width: gridWidth,
        height: gridHeight,
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
        gap: gap,
        pointerEvents: 'none',
      }}
    >
      {cells.map((cell, index) => {
        // Calculate animation progress for this cell
        const cellStartFrame = startFrame + cell.delay;
        const cellEndFrame = cellStartFrame + animDurationFrames;

        // Animation phases:
        // 1. Before start: rest state
        // 2. Animation: scale up and opacity increase
        // 3. After animation: scale back down, maintain some visibility
        // 4. Fade out (optional)

        let scale = 1;
        let opacity = restOpacity;

        if (frame >= cellStartFrame) {
          if (frame <= cellEndFrame) {
            // During animation - ease out for smooth expansion
            const progress = interpolate(
              frame,
              [cellStartFrame, cellEndFrame],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
            );

            // Use easeOut for scale (quick start, slow end)
            const easedProgress = Easing.out(Easing.cubic)(progress);

            // Scale peaks at middle of animation, then settles
            const scaleProgress = interpolate(
              progress,
              [0, 0.4, 1],
              [1, maxScale, 1.2],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
            );
            scale = scaleProgress;

            // Opacity peaks and then settles
            opacity = interpolate(
              progress,
              [0, 0.3, 1],
              [restOpacity, peakOpacity, restOpacity * 2],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
            );
          } else {
            // After animation - settled state
            scale = 1.2;
            opacity = restOpacity * 2;

            // Optional fade out
            if (fadeOut) {
              const fadeStartFrame = startFrame + totalAnimDuration + fadeOutDelay;
              const fadeEndFrame = fadeStartFrame + fps * 0.5;

              if (frame >= fadeStartFrame) {
                opacity = interpolate(
                  frame,
                  [fadeStartFrame, fadeEndFrame],
                  [restOpacity * 2, 0],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
                );
                scale = interpolate(
                  frame,
                  [fadeStartFrame, fadeEndFrame],
                  [1.2, 0.8],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
                );
              }
            }
          }
        }

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShapeRenderer
              shape={shape}
              size={cellSize}
              color={color}
              opacity={opacity}
              scale={scale}
              shadowBlur={shadowBlur * opacity}
              shadowColor={shadowColor}
            />
          </div>
        );
      })}
    </div>
  );
};

export default BackgroundRippleEffect;
